import React, { createContext, useEffect, useRef, useState, useContext, useMemo } from 'react';
import { TicketContext } from '../../app/providers/TicketContext';
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { useNavigate } from 'react-router-dom';
import { safeParseJson } from '../../shared/security/safeJson';
import { redactSensitive } from '../../shared/security/redactSensitive';
import { isEventDisabled } from '../../shared/utils/eventUtils';

export const TicketWebSocketContext = createContext();

// Modül seviyesi guard — StrictMode/HMR double-mount durumunda da
// browser başına tek bir ticket WS bağlantısının açıldığını garanti eder.
let __TICKET_WS_PROVIDER_ACTIVE__ = false;

export const TicketWebSocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [eveId, setEveId] = useState(null);
  const [ticId, setTicId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [notificationCounters, setNotificationCounters] = useState({});
  // NOTE:
  // - Prod'da genelde reverse-proxy üzerinden wss endpoint kullanılır.
  // - ws2 gibi path'ler proxy'de tanımlı değilse bağlantı hiç kurulmaz ve mesaj gelmez.
  // - Bu yüzden URL'i env ile kontrol edilebilir yaptık.
  const wsUrl =
    process.env.REACT_APP_TICKET_WS_URL ||
    process.env.REACT_APP_WS_URL ||
    "wss://v2.soylemez.diagrun.com/ws/";
  const IS_PROD = process.env.NODE_ENV === 'production';
  const [visData, setVisData] = useState(null);
  const [loekz, setLoekz] = useState(null);
  const [ticketsToMoveToTop, setTicketsToMoveToTop] = useState(new Set());
  const [pendingEvents, setPendingEvents] = useState(new Set()); // Bekleyen event'ler
  
  // WidgetWebSocket loglarını da al (WebSocketContext'ten gelecek)
  
  // Network trafiği ve WebSocket mesajları için yeni state'ler
  const [networkLogs, setNetworkLogs] = useState([]);
  const [websocketLogs, setWebsocketLogs] = useState([]);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [logFilters, setLogFilters] = useState({
    showRequests: true,
    showResponses: true,
    showWebSocket: true,
    showErrors: true
  });
  
  const { 
    selectTicket, 
    selectedTicketId, 
    setSelectedTicketId,
    setSelectedTicket,
    events, 
    addEventToCache, 
    deleteEventFromCache, 
    eventCache, 
    setEvents, 
 
    setRightPanelToolbarElements,
    setAllFields,
    setTickets,
    SendEvent,
    setCurrentInputField,
    markSendPerformanceStage
  } = useContext(TicketContext);
  
  // WidgetWebSocket loglarını da al (WebSocketContext'ten gelecek)
  const selectedTicketIdRef = useRef(selectedTicketId);
  const isProcessingRef = useRef(false);
  const processedEventsRef = useRef(new Set());
  const fetchingEventsRef = useRef(new Set());
  const lastFetchTimeRef = useRef({});
  const processedStreamersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isReconnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true); // Component unmount olduğunda reconnect'i durdurmak için

  if (!(processedStreamersRef.current instanceof Map)) {
    processedStreamersRef.current = new Map();
  }
  const navigate = useNavigate();

  // Network log ekleme fonksiyonu
  const addNetworkLog = (type, data, timestamp = Date.now()) => {
    const log = {
      id: Date.now() + Math.random(),
      type,
      data: IS_PROD ? redactSensitive(data) : data,
      timestamp,
      formattedTime: new Date(timestamp).toLocaleTimeString('tr-TR')
    };
    
    setNetworkLogs(prev => [log, ...prev.slice(0, 999)]); // Son 1000 log'u tut
  };

  // WebSocket log ekleme fonksiyonu
  const addWebSocketLog = (type, data, timestamp = Date.now()) => {
    const log = {
      id: Date.now() + Math.random(),
      type,
      data: IS_PROD ? redactSensitive(data) : data,
      timestamp,
      formattedTime: new Date(timestamp).toLocaleTimeString('tr-TR'),
      source: 'TicketWebSocket', // WebSocket kaynağını belirt
      function: getCallingFunction() // Çağıran fonksiyonu belirt
    };
    
    setWebsocketLogs(prev => [log, ...prev.slice(0, 999)]); // Son 1000 log'u tut
  };

  // Çağıran fonksiyonu tespit etme
  const getCallingFunction = () => {
    try {
      const stack = new Error().stack;
      const lines = stack.split('\n');
      
      // TicketWebSocketProvider içindeki fonksiyonları bul
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.includes('TicketWebSocketProvider') || line.includes('connect') || line.includes('onmessage')) {
          continue; // Provider fonksiyonlarını atla
        }
        
        if (line.includes('fetchEventFromServer')) {
          return 'fetchEventFromServer';
        } else if (line.includes('onopen')) {
          return 'WebSocket.onopen';
        } else if (line.includes('onclose')) {
          return 'WebSocket.onclose';
        } else if (line.includes('onerror')) {
          return 'WebSocket.onerror';
        } else if (line.includes('onmessage')) {
          return 'WebSocket.onmessage';
        }
      }
      
      return 'Unknown Function';
    } catch (error) {
      return 'Error Detecting Function';
    }
  };

  // fetchWrapper'ı override et
  const originalFetchWrapper = fetchWrapper;
  const enhancedFetchWrapper = async (processId, body, navigate) => {
    const requestId = Date.now() + Math.random();
    
    // Request log'u ekle
    addNetworkLog('REQUEST', {
      processId,
      body,
      requestId,
      url: process.env.REACT_APP_API_URL || 'API_URL'
    });
    
    try {
      const response = await originalFetchWrapper(processId, body, navigate);
      
      // Response log'u ekle
      addNetworkLog('RESPONSE', {
        processId,
        response,
        requestId,
        success: true
      });
      
      // fetchWrapper zaten debug log ekliyor, burada ek bir şey yapmaya gerek yok
      
      return response;
    } catch (error) {
      // Error log'u ekle
      addNetworkLog('ERROR', {
        processId,
        error: error.message,
        requestId,
        success: false
      });
      
      throw error;
    }
  };

  // Global fetchWrapper'ı override et
  if (!IS_PROD && typeof window !== 'undefined') {
    window.enhancedFetchWrapper = enhancedFetchWrapper;
  }

  useEffect(() => {
    selectedTicketIdRef.current = selectedTicketId;
    processedEventsRef.current.clear();
  }, [selectedTicketId]);

  // Ticket'ı en başa taşıma fonksiyonu
  const moveTicketToTop = (ticketId) => {
    setTicketsToMoveToTop(prev => new Set([...prev, ticketId]));
    
    // Ticket'ları yeniden sırala - güncellenen ticket'ı en başa taşı
    setTickets(prevTickets => {
      const updatedTicket = prevTickets.find(t => String(t.TICID) === String(ticketId));
      if (!updatedTicket) return prevTickets;
      
      const otherTickets = prevTickets.filter(t => String(t.TICID) !== String(ticketId));
      return [updatedTicket, ...otherTickets];
    });
    
    // 8 saniye sonra set'i temizle (animasyon tamamlandıktan sonra)
    setTimeout(() => {
      setTicketsToMoveToTop(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }, 8000);
  };

  const fetchEventFromServer = async (ticId, eveIdParam, streamerIdParam = null) => {
    WS_LOG('fetchEventFromServer', 'Çağrıldı', { ticId, eveIdParam, streamerIdParam, selectedTicketId: selectedTicketIdRef.current });
    // 🎫 TICKET ID BAZLI FİLTRELEME: Sadece seçili ticket için fetch yap
    const currentTicketId = selectedTicketIdRef.current;
    if (!currentTicketId || parseInt(String(currentTicketId)) !== parseInt(String(ticId))) {
      WS_LOG('fetchEventFromServer', 'Atlandı: seçili ticket değil veya eşleşmiyor');
      return;
    }

    const eveIdList = Array.isArray(eveIdParam) ? eveIdParam : [eveIdParam];
    const uniqueEveIds = Array.from(new Set(eveIdList.filter(id => id !== undefined && id !== null))).map(id => String(id));

    if (uniqueEveIds.length === 0) {
      return;
    }

    const sortedNumericIds = uniqueEveIds
      .map(id => parseInt(id, 10))
      .filter(id => !Number.isNaN(id))
      .sort((a, b) => a - b);

    const primaryEveId = sortedNumericIds.length > 0 ? sortedNumericIds[sortedNumericIds.length - 1] : parseInt(uniqueEveIds[uniqueEveIds.length - 1], 10);

    if (!primaryEveId || Number.isNaN(primaryEveId)) {
      return;
    }

    const now = Date.now();
    const lastFetchKey = `eve-${primaryEveId}`;
    const lastFetchTime = lastFetchTimeRef.current[lastFetchKey] || 0;
    const timeSinceLastFetch = now - lastFetchTime;

    if (fetchingEventsRef.current.has(lastFetchKey)) {
      return;
    }

    if (timeSinceLastFetch < 2000) {
      return;
    }

    // Pending events'e ekle - loading baloncuğu gösterilecek
    setPendingEvents(prev => {
      const newSet = new Set(prev);
      uniqueEveIds.forEach(id => newSet.add(id));
      return newSet;
    });

    fetchingEventsRef.current.add(lastFetchKey);
    lastFetchTimeRef.current[lastFetchKey] = now;

    try {
    const requestBody = {
        token: localStorage.getItem('token'),
        process: 100022958,
        TICID: ticId,
        EVEID: primaryEveId,
        layoutParams: ''
      };
      if (uniqueEveIds.length > 0) {
        requestBody.EVEIDS = uniqueEveIds;
      }
      if (streamerIdParam) {
        requestBody.STREAMER_ID = streamerIdParam;
      }

      const result = await enhancedFetchWrapper(100022958, requestBody, navigate);

              if (result.result) {
          // 🎫 TICKET ID BAZLI FİLTRELEME: Sadece seçili ticket için toolbar/body/sendbox güncelle
          const currentTicketIdAfterFetch = selectedTicketIdRef.current;
          if (!currentTicketIdAfterFetch || parseInt(String(currentTicketIdAfterFetch)) !== parseInt(String(ticId))) {
            // Ticket değiştiyse, sadece event'leri cache'e ekle, toolbar/body/sendbox güncelleme
          } else {
            // Seçili ticket hala aynı, toolbar/body/sendbox güncelle
            if (result.new_request) {
              const { ELMNT, DTYPE, DFLEN, ELMDC, CTABL, READONLY, REQUIRED, ACTION } = result.new_request;
              
              // 🚀 currentInputField'ı güncelle - RightPanelSendBox'ı tetikle
              setCurrentInputField(result.new_request);

              // ACTION field'ı varsa SendEvent gönder
              if (ACTION && ACTION.DRC) {
                SendEvent({
                  inputName: ELMNT,
                  message: `-${ACTION.DRC} ${ACTION.data.mission}`,
                }, []);
              }
            }

            if (result.toolbar) {
              setRightPanelToolbarElements(result.toolbar);
            }

            if (result.all_fields) {
              setAllFields(result.all_fields);
            }
          }

        const normalizedEvents = [];

        if (Array.isArray(result.events) && result.events.length > 0) {
          result.events.forEach((serverEvent, index) => {
            const visibleValue = serverEvent.VISIBLE;
            const isVisible = visibleValue === 1 || visibleValue === "1" || visibleValue === true;
            
            const loekzValue = serverEvent.LOEKZ;
            const isDeleted = loekzValue === 1 || loekzValue === "1" || loekzValue === true || 
                              loekzValue === "X" || loekzValue === "x";
            
            // Önce server'dan gelen tüm field'ları olduğu gibi al, sonra
            // override istediğimiz alanları yaz. Bu sayede STYPE gibi yeni
            // server field'ları whitelist eksikliği yüzünden drop olmaz.
            normalizedEvents.push({
              ...serverEvent,
              EVEID: serverEvent.EVEID,
              TICID: serverEvent.TICID,
              MEMID: serverEvent.MEMID,
              ETYPE: serverEvent.ETYPE,
              FLOWID: serverEvent.FLOWID,
              ESOURCEFILE: serverEvent.ESOURCEFILE,
              ANDAT: serverEvent.ANDAT,
              TDESC: serverEvent.TDESC,
              RONLY: serverEvent.RONLY,
              ROWID: serverEvent.ROWID,
              FNAME: serverEvent.FNAME,
              ITYPE: serverEvent.ITYPE,
              FDESC: serverEvent.FDESC,
              FVALUE: serverEvent.FVALUE,
              ALLVALUES: serverEvent.ALLVALUES,
              VALIDATE: serverEvent.VALIDATE,
              VISIBLE: serverEvent.VISIBLE,
              COMPLATED: serverEvent.COMPLATED,
              LOEKZ: serverEvent.LOEKZ,
              LONAM: serverEvent.LONAM,
              LODAT: serverEvent.LODAT,
              member: serverEvent.member,
              position: serverEvent.position,
              INPA_CONF: serverEvent.INPA_CONF,
              ATTACHES: serverEvent.ATTACHES || [],
              TOWNER_IMAGE: serverEvent.TOWNER_IMAGE,
              PHOTO_SWITCH: serverEvent.PHOTO_SWITCH,
              UNAME_SWITCH: serverEvent.UNAME_SWITCH,
              FNAME_SWITCH: serverEvent.FNAME_SWITCH,
              FVALUE_SWITCH: serverEvent.FVALUE_SWITCH,
              TDESC_SWITCH: serverEvent.TDESC_SWITCH,
              ESOURCEFILE_SWITCH: serverEvent.ESOURCEFILE_SWITCH,
              // STYPE: explicit korunur (case farkları için fallback)
              STYPE: serverEvent.STYPE ?? serverEvent.stype ?? serverEvent.Stype ?? null,
            });
          });
        } else if (result.event) {
          normalizedEvents.push(result.event);
        } else if (result.data && result.data.event) {
          normalizedEvents.push(result.data.event);
        } else {
          normalizedEvents.push({
            EVEID: primaryEveId,
            TICID: parseInt(ticId, 10),
            FNAME: 'WEBSOCKET_EVENT',
            FVALUE: 'WebSocket\'ten gelen event',
            TDESC: 'WebSocket event',
            ANDAT: new Date().toLocaleString('tr-TR'),
            VALIDATE: 1,
            UNAME_SWITCH: 1,
            TDESC_SWITCH: 1,
            FVALUE_SWITCH: 1,
            PHOTO_SWITCH: 0,
            ESOURCEFILE_SWITCH: 0,
            position: 'L',
            member: {
              uname: 'WebSocket User',
              memid: 'websocket_user',
              pp_path: null
            },
            ATTACHES: []
          });
        }

        if (normalizedEvents.length > 0) {
          // 🚀 new_request bilgisini de cache'e ekle
          const additionalDataWithNewRequest = {
            ...result,
            new_request: result.new_request // new_request'i de ekle
          };
          
          normalizedEvents.forEach(eventData => {
            if (eventData && eventData.EVEID) {
              addEventToCache(ticId, eventData, additionalDataWithNewRequest);
            }
          });

          // Ticket'ın tarih bilgisini güncelle (LAST_EVENTDATE güncellenmiyor, sadece local state)
          setTickets(prevTickets => {
            return prevTickets.map(ticket => {
              if (String(ticket.TICID) === String(ticId)) {
                return {
                  ...ticket,
                  ANDAT: normalizedEvents[normalizedEvents.length - 1].ANDAT || new Date().toISOString(),
                  lastEventDate: normalizedEvents[normalizedEvents.length - 1].ANDAT || new Date().toISOString()
                  // LAST_EVENTDATE burada güncellenmiyor, ticket_update mesajında geliyor
                };
              }
              return ticket;
            });
          });

          markSendPerformanceStage(ticId, 'wsFetched', {
            streamerId: streamerIdParam,
            fetchedIds: normalizedEvents.map((ev) => ev.EVEID),
          });

          // 🎫 TICKET ID BAZLI FİLTRELEME: Sadece seçili ticket için events güncelle
          const currentTicketIdForEvents = selectedTicketIdRef.current;
          if (currentTicketIdForEvents && parseInt(String(currentTicketIdForEvents), 10) === parseInt(String(ticId), 10)) {
            setEvents(prevEvents => {
              // Tüm event'leri tut (disabled olanlar dahil). LOEKZ/VISIBLE filtrelemesi yapılmaz.
              const eventMap = new Map();

              // Mevcut state'deki tüm event'leri ekle
              prevEvents.forEach(event => {
                eventMap.set(String(event.EVEID), event);
              });

              normalizedEvents.forEach(eventData => {
                const existing = eventMap.get(String(eventData.EVEID));
                if (existing && isEventDisabled(existing)) {
                  return;
                }
                eventMap.set(String(eventData.EVEID), eventData);
              });

              const sortedEvents = Array.from(eventMap.values()).sort((a, b) => {
                const aId = parseInt(a.EVEID) || 0;
                const bId = parseInt(b.EVEID) || 0;
                return aId - bId;
              });

              return sortedEvents;
            });
          } else {
          }
        }
      }
    } catch (error) {
    } finally {
      fetchingEventsRef.current.delete(lastFetchKey);
      // Pending events'ten çıkar - loading baloncuğu kaldırılacak
      setPendingEvents(prev => {
        const newSet = new Set(prev);
        uniqueEveIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  const cleanupProcessedStreamers = (ttl = 60000) => {
    const now = Date.now();
    processedStreamersRef.current.forEach((ts, key) => {
      if (now - ts > ttl) {
        processedStreamersRef.current.delete(key);
      }
    });
  };

  const handleEventBatch = (batchData) => {
    WS_LOG('handleEventBatch', 'Çağrıldı', { events: batchData?.events?.length, streamer_id: batchData?.streamer_id });
    if (!batchData || !Array.isArray(batchData.events) || batchData.events.length === 0) {
      WS_WARN('handleEventBatch', 'Boş veya geçersiz batch', { batchData: !!batchData, events: batchData?.events });
      const emptyStreamerId = String(batchData?.streamer_id || batchData?.STREAMER_ID || '');
      if (emptyStreamerId) {
        processedStreamersRef.current.set(emptyStreamerId, Date.now());
      }
      return;
    }
    const streamerId = String(batchData.streamer_id || batchData.STREAMER_ID || '');

    const lastProcessedAt = streamerId ? processedStreamersRef.current.get(streamerId) : null;
    if (streamerId && lastProcessedAt && Date.now() - lastProcessedAt < 3000) {
      return;
    }

    cleanupProcessedStreamers();

    if (!Array.isArray(batchData.events) || batchData.events.length === 0) {
      if (streamerId) {
        processedStreamersRef.current.set(streamerId, Date.now());
      }
      return;
    }

    if (streamerId && processedStreamersRef.current.has(streamerId)) {
      return;
    }

    const eventsByTicket = batchData.events.reduce((acc, evt) => {
      const ticketId = evt.ticid || evt.TICID;
      const eventId = evt.eveid || evt.EVEID;

      if (!ticketId || !eventId) {
        return acc;
      }

      const key = String(ticketId);
      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(String(eventId));
      return acc;
    }, {});

    Object.entries(eventsByTicket).forEach(([ticketId, eventIds]) => {
      const idsToFetch = eventIds.filter(id => !processedEventsRef.current.has(String(id)));

      const shouldRefetchEvenIfProcessed = idsToFetch.length === 0 && eventIds.length > 0;

      if (idsToFetch.length === 0 && !shouldRefetchEvenIfProcessed) {
        if (streamerId) {
          processedStreamersRef.current.set(streamerId, Date.now());
        }
        return;
      }

      // 🎫 TICKET ID BAZLI FİLTRELEME: Sadece seçili ticket için toolbar/body/sendbox güncelle
      const isCurrentTicket = selectedTicketIdRef.current &&
        parseInt(selectedTicketIdRef.current, 10) === parseInt(ticketId, 10);

      // Ticket listesini güncelle (tüm ticket'lar için)
      moveTicketToTop(ticketId);

      // Eğer seçili ticket değilse, sadece notification counter'ı artır
      if (!isCurrentTicket) {
        setNotificationCounters(prev => {
          const newCounters = { ...prev };
          newCounters[ticketId] = (newCounters[ticketId] || 0) + idsToFetch.length;
          return newCounters;
        });
        // Seçili ticket değilse, event'leri işaretle ama fetch etme
        idsToFetch.forEach(id => processedEventsRef.current.add(String(id)));
        if (streamerId) {
          processedStreamersRef.current.set(streamerId, Date.now());
        }
        return; // Seçili ticket değilse, toolbar/body/sendbox güncellemesi yapma
      }

      // Seçili ticket ise, toolbar/body/sendbox güncellemesi yap
      
      markSendPerformanceStage(ticketId, 'wsMessage', {
        streamerId: batchData.streamer_id || batchData.STREAMER_ID || null,
        eventCount: idsToFetch.length,
      });

      idsToFetch.forEach(id => processedEventsRef.current.add(String(id)));

      const fetchIds = idsToFetch.length > 0 ? idsToFetch : eventIds;

      // fetchEventFromServer çağrıldığında, toolbar/body/sendbox otomatik güncellenecek
      fetchEventFromServer(ticketId, fetchIds, streamerId).finally(() => {
        if (streamerId) {
          processedStreamersRef.current.set(streamerId, Date.now());
        }
      });
    });
  };

  const WS_LOG = (tag, ...args) => {
    //console.log(`%c[WS:${tag}]`, 'color:#6366f1;font-weight:bold', ...args);
  };
  const WS_WARN = (tag, ...args) => {
    //console.warn(`%c[WS:${tag}]`, 'color:#f59e0b;font-weight:bold', ...args);
  };
  const WS_ERR = (tag, ...args) => {
    // console.error(`%c[WS:${tag}]`, 'color:#ef4444;font-weight:bold', ...args);
  };

  const connect = () => {
    WS_LOG('connect', 'connect() çağrıldı', { wsUrl, readyState: socketRef.current?.readyState });
    // Eğer zaten bağlıysa veya reconnect yapılıyorsa, tekrar bağlanma
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      WS_LOG('connect', 'Zaten bağlı, atlanıyor');
      return;
    }

    // Eğer reconnect yapılıyorsa, yeni bağlantı kurma
    if (isReconnectingRef.current) {
      WS_LOG('connect', 'Reconnect devam ediyor, atlanıyor');
      return;
    }

    // Eski bağlantıyı temizle
    if (socketRef.current) {
      try {
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        if (socketRef.current.readyState !== WebSocket.CLOSED) {
          socketRef.current.close();
        }
      } catch (e) {
        // Eski bağlantıyı temizlerken hata olabilir, önemli değil
      }
    }

    try {
      WS_LOG('connect', 'WebSocket oluşturuluyor', wsUrl);
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;
      ws.onopen = () => {
        WS_LOG('onopen', 'Bağlantı açıldı', { url: wsUrl, readyState: ws.readyState });
        setConnected(true);
        isReconnectingRef.current = false;
        reconnectAttemptsRef.current = 0; // Başarılı bağlantıda reconnect sayacını sıfırla
        processedEventsRef.current.clear();
        
        // Reconnect timeout'unu temizle
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        addWebSocketLog('CONNECTION', {
          status: 'OPEN',
          url: wsUrl,
          timestamp: new Date().toISOString(),
          connectionId: Date.now(),
          readyState: ws.readyState
        });
      };

      ws.onmessage = (event) => {
        WS_LOG('onmessage', 'Ham mesaj alındı', { length: event.data?.length, preview: String(event.data).slice(0, 200) });
        const parsed = safeParseJson(event.data, 200000);
        if (!parsed.ok || !parsed.value || typeof parsed.value !== 'object') {
          WS_WARN('onmessage', 'Parse hatası veya geçersiz veri', { ok: parsed.ok, error: parsed.error });
          return;
        }
        const data = parsed.value;
        WS_LOG('onmessage', 'Parse başarılı', { type: data?.type, id: data?.id, keys: Object.keys(data || {}) });

        if (data && data.id) {
          ws.send(JSON.stringify({ type: "ack", id: data.id }));
        }

        // WebSocket mesajını logla
        addWebSocketLog('MESSAGE', {
          rawData: IS_PROD ? undefined : event.data,
          parsedData: data,
          timestamp: new Date().toISOString(),
          messageType: typeof data.type === 'string' ? data.type : 'UNKNOWN',
          messageSize: typeof event.data === 'string' ? event.data.length : 0,
          connectionId: Date.now()
        });

        if (data.type === "event_batch") {
          WS_LOG('onmessage', 'event_batch işleniyor', { events: data.events?.length, streamer_id: data.streamer_id });
          handleEventBatch(data);
        } else if (data.type === "event_update") {
          WS_LOG('onmessage', 'event_update işleniyor', { ticid: data.data?.ticid, eveid: data.data?.eveid });
          if (!data.data || typeof data.data !== 'object' || !data.data.ticid) {
            return;
          }

          const incomingEventId = data.data?.eveid;
          if (incomingEventId && processedEventsRef.current.has(String(incomingEventId))) {
            return;
          }

          const messageTicketId = String(data.data.ticid);
          const currentTicketId = selectedTicketIdRef.current ? String(selectedTicketIdRef.current) : null;
          
          const isCurrentTicket = currentTicketId && parseInt(currentTicketId) === parseInt(messageTicketId);

          // Ticket listesini güncelle (tüm ticket'lar için)
          setTickets(prevTickets => {
            return prevTickets.map(ticket => {
              if (String(ticket.TICID) === messageTicketId) {
                return {
                  ...ticket,
                  lastEventDate: data.data.timestamp || new Date().toISOString(),
                  LAST_EVENTDATE: data.data.timestamp || new Date().toISOString()
                };
              }
              return ticket;
            });
          });

          moveTicketToTop(messageTicketId);

          // Eğer seçili ticket değilse, sadece notification counter'ı artır
          if (!isCurrentTicket) {
            setNotificationCounters(prev => {
              const newCounters = {
                ...prev,
                [messageTicketId]: (prev[messageTicketId] || 0) + 1
              };
              return newCounters;
            });
            // Event'i işaretle ama fetch etme
            processedEventsRef.current.add(String(incomingEventId));
            return; // Seçili ticket değilse, toolbar/body/sendbox güncellemesi yapma
          }

          // Seçili ticket ise, toolbar/body/sendbox güncellemesi yap
          
          setEveId(data.data.eveid);
          setTicId(data.data.ticid);
          setVisData(data.data.visible);
          setLoekz(data.data.loekz);

          markSendPerformanceStage(messageTicketId, 'wsMessage', {
            streamerId: data.data?.streamer_id || data.data?.STREAMER_ID || null,
            eventCount: 1,
          });

          processedEventsRef.current.add(String(incomingEventId));
          
          const wsLoekzValue = data.data.loekz;
          const wsVisibleValue = data.data.visible;

          const wsIsDeleted = wsLoekzValue === 1 || wsLoekzValue === "1" || wsLoekzValue === true ||
                              wsLoekzValue === "X" || wsLoekzValue === "x";
          const wsIsExplicitlyInvisible = wsVisibleValue !== undefined && wsVisibleValue !== null &&
                              (wsVisibleValue === 0 || wsVisibleValue === "0" || wsVisibleValue === false);

          if (wsIsDeleted || wsIsExplicitlyInvisible) {
            // Event'i listede tut ama disabled olarak işaretle (gri + tıklanamaz)
            deleteEventFromCache(data.data.ticid, data.data.eveid);
            return;
          }

          isProcessingRef.current = true;

          fetchEventFromServer(
            data.data.ticid,
            data.data.eveid,
            data.data.streamer_id || data.data.STREAMER_ID || null
          ).finally(() => {
            isProcessingRef.current = false;
          });
        } else if (data.type === "event_delete") {
          WS_LOG('onmessage', 'event_delete işleniyor', data.data);
          if (!data.data?.ticid) {
            WS_WARN('onmessage', 'event_delete: ticid yok');
            return;
          }

          const messageTicketId = String(data.data.ticid);
          const currentTicketId = selectedTicketIdRef.current ? String(selectedTicketIdRef.current) : null;

          const isCurrentTicket = currentTicketId && parseInt(currentTicketId) === parseInt(messageTicketId);

          // Event'i silmek yerine disable olarak işaretle (LOEKZ='X')
          if (isCurrentTicket) {
            deleteEventFromCache(data.data.ticid, data.data.eveid);
          }
        } else if (data.type === "ticket_update") {
          WS_LOG('onmessage', 'ticket_update işleniyor', data.data);
          if (!data.data?.ticid) {
            WS_WARN('onmessage', 'ticket_update: ticid yok');
            return;
          }

          const updatedTicketId = String(data.data.ticid);
          const currentTicketId = selectedTicketIdRef.current ? String(selectedTicketIdRef.current) : null;
          
          if (data.data.LOEKZ === "X" || data.data.LOEKZ === "x" || data.data.TSTAT === "0" || data.data.TSTAT === 0) {
            setTickets(prevTickets => 
              prevTickets.filter(ticket => String(ticket.TICID) !== updatedTicketId)
            );
            
            if (currentTicketId && parseInt(currentTicketId) === parseInt(updatedTicketId)) {
              setEvents([]);
              setSelectedTicket(null);
              setSelectedTicketId(null);
              setRightPanelToolbarElements([]);
              setAllFields(null);
              setCurrentInputField(null);
            }
            
            return;
          }
          
          if (data.data.LAST_EVENTDATE) {
            moveTicketToTop(data.data.ticid);
          }

          setTickets((prev) => {
            const tid = String(data.data.ticid);
            const idx = prev.findIndex((t) => String(t.TICID) === tid);
            if (idx === -1) return prev;
            const updated = { ...prev[idx] };
            if (data.data.TSTAT !== undefined) updated.TSTAT = data.data.TSTAT;
            if (data.data.LAST_EVENTDATE) updated.LAST_EVENTDATE = data.data.LAST_EVENTDATE;
            if (data.data.TDESC !== undefined) updated.TDESC = data.data.TDESC;
            const next = [...prev];
            next[idx] = updated;
            return next;
          });
        } else if (data.type === "ticket_delete") {
          WS_LOG('onmessage', 'ticket_delete işleniyor', data.data);
          setTickets((prev) =>
            prev.filter((t) => String(t.TICID) !== String(data.data?.ticid)),
          );
        } else {
          WS_LOG('onmessage', 'Bilinmeyen mesaj tipi', { type: data.type, data });
        }
      };

      ws.onclose = (event) => {
        WS_LOG('onclose', 'Bağlantı kapandı', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        setConnected(false);
        addWebSocketLog('CONNECTION', {
          status: 'CLOSED',
          code: event.code,
          reason: event.reason,
          timestamp: new Date().toISOString(),
          wasClean: event.wasClean,
          connectionId: Date.now()
        });

        // Eğer temiz bir kapanış değilse ve reconnect yapılması gerekiyorsa, yeniden bağlan
        // Code 1000 = Normal Closure (temiz kapanış), bu durumda reconnect yapma
        // Code 1006 = Abnormal Closure (anormal kapanış), reconnect yap
        if (event.code !== 1000 && shouldReconnectRef.current && !isReconnectingRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = (error) => {
        WS_ERR('onerror', 'WebSocket hatası', { error, readyState: ws?.readyState });
        setConnected(false);
        // onerror'da error.message genellikle undefined olur, bu yüzden sadece log'la
        // Console'a sürekli hata basmamak için sadece bir kez log'la
        if (!isReconnectingRef.current) {
        addWebSocketLog('ERROR', {
            error: 'WebSocket connection error',
          timestamp: new Date().toISOString(),
          connectionId: Date.now(),
          readyState: ws.readyState
        });
          
          // Hata durumunda reconnect yap (eğer zaten reconnect yapılmıyorsa)
          if (shouldReconnectRef.current && !isReconnectingRef.current) {
            scheduleReconnect();
          }
        }
      };

    } catch (error) {
      setConnected(false);
      isReconnectingRef.current = false;
      
      // Bağlantı kurulurken hata olursa reconnect yap
      if (shouldReconnectRef.current && !isReconnectingRef.current) {
        scheduleReconnect();
      }
    }
  };

  // Reconnect mekanizması - Exponential backoff ile
  const scheduleReconnect = () => {
    // Eğer zaten reconnect yapılıyorsa veya reconnect durdurulmuşsa, yapma
    if (isReconnectingRef.current || !shouldReconnectRef.current) {
      return;
    }

    // Maksimum 10 deneme (yaklaşık 5 dakika sonra durur)
    if (reconnectAttemptsRef.current >= 10) {
      isReconnectingRef.current = false;
      return;
    }

    isReconnectingRef.current = true;
    reconnectAttemptsRef.current += 1;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 64s, 128s, 256s, 512s
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000); // Maksimum 30 saniye

    addWebSocketLog('RECONNECT', {
      attempt: reconnectAttemptsRef.current,
      delay: delay,
      timestamp: new Date().toISOString()
    });

    reconnectTimeoutRef.current = setTimeout(() => {
      if (shouldReconnectRef.current) {
        isReconnectingRef.current = false;
        connect();
      }
    }, delay);
  };

  useEffect(() => {
    // StrictMode / HMR double-mount koruması: bir tarayıcı sekmesinde aynı
    // anda yalnızca tek bir ticket WS bağlantısı açılabilir.
    if (__TICKET_WS_PROVIDER_ACTIVE__) {
      // eslint-disable-next-line no-console
      console.warn('[TicketWebSocketProvider] Başka aktif instance var, ikinci bağlantı engellendi.');
      return undefined;
    }
    __TICKET_WS_PROVIDER_ACTIVE__ = true;

    shouldReconnectRef.current = true;

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // Aktif bağlantı zaten var — yine de cleanup için guard'ı kapatma.
      return () => {
        __TICKET_WS_PROVIDER_ACTIVE__ = false;
      };
    }

    connect();

    return () => {
      // Component unmount olduğunda reconnect'i durdur
      shouldReconnectRef.current = false;

      // Reconnect timeout'unu temizle
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // WebSocket bağlantısını kapat
      if (socketRef.current) {
        try {
          socketRef.current.onclose = null;
          socketRef.current.onerror = null;
          socketRef.current.onopen = null;
          socketRef.current.onmessage = null;
          if (socketRef.current.readyState !== WebSocket.CLOSED) {
            socketRef.current.close(1000, 'Component unmounting'); // Normal closure
          }
        } catch (e) {
          // Hata olursa önemli değil
        }
        socketRef.current = null;
      }

      __TICKET_WS_PROVIDER_ACTIVE__ = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModalOk = () => {
    setShowModal(false);
    if (modalData && modalData.ticid) {
      selectTicket(modalData.ticid);
    }
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setModalData(null);
  };

  // Context value'yu useMemo ile sarmalayalım - infinite loop'u önlemek için
  const contextValue = useMemo(() => ({
    connected, 
    eveId, 
    ticId, 
    notificationCounters, 
    setNotificationCounters,
    visData,
    loekz,
    fetchEventFromServer,
    ticketsToMoveToTop,
    moveTicketToTop,
    pendingEvents,
    networkLogs,
    websocketLogs,
    showNetworkModal,
    setShowNetworkModal,
    logFilters,
    setLogFilters,
    addNetworkLog,
    addWebSocketLog
  }), [
    connected, 
    eveId, 
    ticId, 
    notificationCounters, 
    visData,
    loekz,
    fetchEventFromServer,
    ticketsToMoveToTop,
    moveTicketToTop,
    pendingEvents,
    networkLogs,
    websocketLogs,
    showNetworkModal,
    logFilters,
    addNetworkLog,
    addWebSocketLog
  ]);

  return (
    <TicketWebSocketContext.Provider value={contextValue}>
      {children}
    </TicketWebSocketContext.Provider>
  );
}; 